import React, { useEffect, useState } from "react";
import { Modal, Form, Input, InputNumber, Select, DatePicker, Switch, Row, Col } from "antd";
import moment from "moment";
import { httpPost, httpPut, customBaseUrl } from "../../../store/http";
import NotificationManager from "react-notifications/lib/NotificationManager";

const { Option } = Select;
const { TextArea } = Input;

const RewardRuleModal = ({ visible, onClose, onSuccess, initialData }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      if (initialData) {
        form.setFieldsValue({
          ...initialData,
          dates: [
            moment(initialData.startDate),
            moment(initialData.endDate),
          ],
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ isActive: true });
      }
    }
  }, [visible, initialData, form]);

  const handleFinish = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        name: values.name,
        description: values.description,
        triggerType: values.triggerType,
        triggerConfig: values.triggerConfig || "",
        rewardType: values.rewardType,
        rewardValue: values.rewardValue || 0,
        maxRewardAmount: values.maxRewardAmount || 0,
        minTransactionAmount: values.minTransactionAmount || 0,
        maxTransactionAmount: values.maxTransactionAmount || 0,
        maxRewardAmountPerPeriod: values.maxRewardAmountPerPeriod || 0,
        periodWindowDays: values.periodWindowDays || 0,
        allowedUsage: values.allowedUsage || "ANY",
        maxGrantsPerUser: values.maxGrantsPerUser || 1,
        totalPoolLimit: values.totalPoolLimit || 0,
        startDate: values.dates[0].toISOString(),
        endDate: values.dates[1].toISOString(),
        expiryDays: values.expiryDays || 0,
        isActive: values.isActive !== undefined ? values.isActive : true,
        customerCategories: values.customerCategories || "",
        createdBy: "ADMIN",
      };

      let res;
      if (initialData?.id) {
        res = await httpPut(`${customBaseUrl.tranv2}/api/v1/admin/reward/rules/${initialData.id}`, payload);
      } else {
        res = await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/reward/rules`, payload);
      }

      if (res?.success || res?.status) {
        NotificationManager.success(`Reward rule ${initialData ? "updated" : "created"} successfully`);
        onSuccess();
      } else {
        NotificationManager.error(res?.message || "Failed to save reward rule");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("An error occurred while saving the rule");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={initialData ? "Edit Reward Rule" : "Create Reward Rule"}
      visible={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={submitting}
      width={800}
      okText={initialData ? "Update Rule" : "Create Rule"}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="name" label="Rule Name" rules={[{ required: true }]}>
              <Input placeholder="e.g. New User Signup Bonus" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="isActive" label="Status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="description" label="Description">
          <TextArea rows={2} placeholder="Brief description of the reward rule" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="triggerType" label="Trigger Type" rules={[{ required: true }]}>
              <Select placeholder="Select trigger type">
                <Option value="SIGNUP">SIGNUP</Option>
                <Option value="FIRST_TRANSACTION">FIRST TRANSACTION</Option>
                <Option value="NTH_TRANSACTION">NTH TRANSACTION</Option>
                <Option value="MIN_AMOUNT">MIN AMOUNT</Option>
                <Option value="BILLS_PAYMENT_CASHBACK">BILLS PAYMENT CASHBACK</Option>
                <Option value="MONTHLY_ACTIVE">MONTHLY ACTIVE</Option>
                <Option value="FIRST_DEPOSIT">FIRST DEPOSIT</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="triggerConfig" label="Trigger Config">
              <Input placeholder="Additional config (JSON string or values)" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="rewardType" label="Reward Type" rules={[{ required: true }]}>
              <Select placeholder="Select reward type">
                <Option value="FIXED">FIXED AMOUNT</Option>
                <Option value="PERCENTAGE">PERCENTAGE</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="rewardValue" label="Reward Value" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="maxRewardAmount" label="Max Reward (If Percentage)">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="maxGrantsPerUser" label="Max Grants Per User">
              <InputNumber style={{ width: "100%" }} min={1} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="totalPoolLimit" label="Total Pool Limit (Budget)">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="minTransactionAmount" label="Min Transaction Amount">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="maxTransactionAmount" label="Max Transaction Amount">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="maxRewardAmountPerPeriod" label="Max Reward Amount Per Period">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="periodWindowDays" label="Period Window (Days)">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="dates" label="Validity Period" rules={[{ required: true }]}>
              <DatePicker.RangePicker style={{ width: "100%" }} showTime />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="expiryDays" label="Reward Expiry Days (0 for never)">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="customerCategories" label="Target Customer Categories" rules={[{ required: true }]}>
              <Select placeholder="Select customer categories">
                <Option value="ALL">ALL</Option>
                <Option value="A">A</Option>
                <Option value="B">B</Option>
                <Option value="C">C</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="allowedUsage" label="Allowed Usage">
              <Input placeholder="e.g. ALL, BILLS, TRANSFER" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default RewardRuleModal;
